import React, { useState, useCallback, useRef, useEffect } from "react";
import { FaShare, FaVial, FaCopy } from "react-icons/fa";
import {
  topicIcon,
  renderCardIcon,
  renderIcon,
  internalTestingIcon,
  presentationIcon,
  latestVersionIcon,
  dialerIcon,
} from "../common/IconLibrary";
import ContextMenu from "../common/ContextMenu";
import { useNavigate } from "react-router-dom";
import { useToast } from "../common/ToastContext";
import PinButton from "../common/PinButton";
import usePins from "../../hooks/usePins";
import {
  duplicateTopic,
  getTopicCardDataForKey,
} from "../../services/topicService";

const TopicCard = ({
  item = {},
  onClick,
  hideContextMenu = false,
  height = null,
  highlightVersions = [],
  compact = false,
  hidePin = false,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [isContextMenuInteraction, setIsContextMenuInteraction] =
    useState(false);
  const [onDemandVersion, setOnDemandVersion] = useState(null);
  const [championsVersion, setChampionsVersion] = useState(null);
  const [presentationVersion, setPresentationVersion] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [tags, setTags] = useState(null);
  const parentRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isPinned, togglePin, canPin } = usePins("topics");

  useEffect(() => {
    const fetchDataByVisibility = async () => {
      try {
        await getTopicCardDataForKey(item.key).then((data) => {
          setLatestVersion(data?.visibilities?.latest || null);
          setOnDemandVersion(data?.visibilities["on-demand"] || null);
          setChampionsVersion(data?.visibilities?.champions || null);
          setPresentationVersion(data?.visibilities?.presentation || null);
          setTags(data.tags || null);
        });
      } catch (error) {
        console.error("Error fetching visibility for prompts:", error);
        showToast("Failed to fetch visibility for prompts", "error");
      }
    };
    if (item?.key) {
      fetchDataByVisibility();
    }
  }, [item?.key, showToast]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setContextMenu({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      setIsContextMenuInteraction(true);
    }
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setIsContextMenuInteraction(false);
  }, []);

  const handleCardClick = (event) => {
    if (!isContextMenuInteraction) {
      onClick(event);
    }
  };

  const handleDuplicate = useCallback(async () => {
    try {
      if (!item?.key) {
        showToast("Cannot duplicate: Invalid topic key", "error");
        return;
      }
      const duplicated = await duplicateTopic(item.key);

      closeContextMenu();
      showToast(
        `Topic duplicated successfully as ${duplicated.name}`,
        "success"
      );
      window.dispatchEvent(
        new CustomEvent("topicCreated", {
          detail: null, // don't send the topic along, otherwise, it will auto-open
        })
      );
    } catch (error) {
      console.error("Error duplicating topic:", error);
      showToast("Failed to duplicate topic", "error");
    }
  }, [item?.key, closeContextMenu, showToast]);

  const contextMenuItems = [
    {
      label: "Share",
      icon: FaShare,
      onClick: () => {
        const url = `${window.location.origin}/topics/?key=${item.key}`;
        navigator.clipboard.writeText(url);
        closeContextMenu();
        showToast("Link copied to clipboard", "success");
      },
    },
    {
      label: "Test",
      icon: FaVial,
      onClick: () => {
        navigate("/ondemand", { state: { topic: item } });
        closeContextMenu();
      },
    },
    item?.key && {
      label: "Duplicate",
      icon: FaCopy,
      onClick: handleDuplicate,
    },
  ].filter(Boolean);

  const tip = (label, version) => {
    switch (label) {
      case "Latest":
        return `Version ${version} is the latest version`;
      case "Internal":
      case "Champion":
      case "On-Demand":
        if (version) {
          return `Version ${version} is being used for ${label} validation`;
        }
        return `A version is not specified for ${label} validation`;
      case "Presentation":
      case "External":
        if (version) {
          return `Version ${version} is available for ${label} validation`;
        }
        return `A version is not specified for ${label} validation`;
      default:
        return "";
    }
  };

  const versionText = (version, max = 3) => {
    if (typeof version === "object") {
      const copyVer = JSON.parse(JSON.stringify(version));
      const last = copyVer || [];
      if (last.length === 0) return "--";
      return copyVer.splice(0, max)?.join(", ");
    }
    return version || "--";
  };

  const renderVersionInfo = (label, version, icon) => (
    <div
      className={`flex items-center p-1 pl-2 ${
        highlightVersions.indexOf(label) !== -1
          ? "bg-fuchsia-500/20 rounded-md"
          : ""
      }`}
    >
      <div className="text-gray-400 mr-2">{renderIcon(icon, 18, 18)}</div>
      <span
        className={`${
          versionText(version) !== "--" ? "text-gray-300" : "text-gray-500/50"
        } text-sm`}
        title={tip(label, version)}
      >
        {versionText(version)}
      </span>
    </div>
  );

  return (
    <div
      className={`card-glass ${height ? "" : "h-full"} ${
        compact ? "p-3 px-4" : ""
      }`}
      style={height ? { height } : {}}
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      ref={parentRef}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center mb-2">
          <span className="text-gray-300 mr-2">
            {renderCardIcon(topicIcon, 24)}
          </span>
          <div className="flex items-center h-[54px]">
            <h3
              className={`${
                compact ? "text-lg" : "text-xl"
              } font-semibold text-gray-100 text-pretty`}
            >
              {item?.name}
            </h3>
          </div>
        </div>
        <div className="relative group flex-grow">
          <p
            className={`text-gray-300 mb-4 line-clamp-3 overflow-hidden ${
              compact ? "text-xs" : ""
            }`}
          >
            {item?.description}
          </p>
          <div className={`hint-glass ${compact ? "text-xs" : ""}`}>
            {item?.description}
          </div>
        </div>
        <div className="mt-auto">
          {!compact && (
            <div className="grid grid-cols-2 gap-1 mb-4">
              {renderVersionInfo("Latest", latestVersion, latestVersionIcon)}
              {renderVersionInfo("On-Demand", onDemandVersion, dialerIcon)}
              {renderVersionInfo(
                "Champion",
                championsVersion,
                internalTestingIcon
              )}
              {renderVersionInfo(
                "Presentation",
                presentationVersion,
                presentationIcon
              )}
            </div>
          )}
          <div className="flex items-start justify-between gap-2 mt-2 min-h-8">
            <div className="flex flex-wrap">
              {item?.imported && (
                <span
                  className={`bg-amber-600 text-white ${
                    compact ? "text-xs px-1.5" : "text-xs px-2"
                  } py-1 rounded-full mr-2 mb-2`}
                  title="This topic is an import or contains imported versions"
                >
                  Imported
                </span>
              )}
              {item?.tags?.map(
                (tag) =>
                  tag && (
                    <span
                      key={tag.id}
                      className={`bg-blue-600 text-white ${
                        compact ? "text-xs px-1.5" : "text-xs px-2"
                      } py-1 rounded-full mr-2 mb-2`}
                    >
                      {tag.name}
                    </span>
                  )
              )}
            </div>
            {!hidePin && !item?.deleted_at && (
              <PinButton
                className="flex-shrink-0"
                size={18}
                pinned={isPinned(item?.key)}
                disabled={!canPin}
                onToggle={() => togglePin(item?.key)}
              />
            )}
          </div>
          {item?.deleted_at && (
            <div className="mt-2 text-red-500 text-sm font-semibold">
              Deleted{" "}
              {item?.deleted_at
                ? new Date(item.deleted_at).toLocaleString()
                : ""}
            </div>
          )}
        </div>
        {!hideContextMenu && contextMenu && (
          <ContextMenu
            items={contextMenuItems}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={closeContextMenu}
            parentRef={parentRef}
          />
        )}
      </div>
    </div>
  );
};

export default TopicCard;
